import React from 'react'
import { useIntl } from 'react-intl'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type {
  CruiseItem,
  FlightItem,
  LETripItem,
  TourItem,
} from 'tripPlanner/types/tripItem'
import { getOfferPagePrefilledStateParams } from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: Extract<LETripItem, CruiseItem | TourItem | FlightItem>
  restoreCartUrl: string
}

const UNSUPPORTED_ERR_MESSAGE =
  'Tour V2 & New Cruises do not have bundled flights'

function BookTourFlight(props: Props) {
  const intl = useIntl()
  const { item, restoreCartUrl } = props

  const { closeModal, goBack } = useModalCallbacks()

  let unbundledText: string
  let unbundledURL: string

  if (item.type === 'TOUR' || item.type === 'CRUISE') {
    if (item.savedItemData.offerType === 'tour') {
      const { searchParams, hash } = getOfferPagePrefilledStateParams(item, {
        scrollToOptions: true,
      })
      const queryString = searchParams.toString()
      const URLSuffix = queryString ? `?${queryString}` : ''

      unbundledText = intl.formatMessage({
        id: 'tO1+o5TPH7',
        defaultMessage: 'No, book tour only',
        description: 'Button to book tour without bundled flights',
      })
      unbundledURL = `/offer/tour/${item.code}${URLSuffix}${hash}`
    } else {
      throw new Error(UNSUPPORTED_ERR_MESSAGE)
    }
  } else {
    const data = structuredClone(item.savedItemData)
    delete data.tourBundle

    unbundledText = intl.formatMessage({
      id: 'l/9evodaaU',
      defaultMessage: 'No, book flights only',
      description: 'Button to book flights without bundled tour',
    })
    unbundledURL = buildStandaloneFlightSearchURL(data)
  }

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'QPqyU2ywcf',
          defaultMessage: 'Book tour and flights package?',
          description: 'Modal title asking if user wants to book tour and flights together',
        })}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large" as="p">
            {intl.formatMessage({
              id: '2ndWavlUhU',
              defaultMessage: "You've added this order as a tour and flights package.",
              description: 'Body text explaining the user added a tour and flights bundle',
            })}
          </BodyText>
          <BodyText variant="large" as="p">
            {intl.formatMessage({
              id: 'DEhNCXRT9L',
              defaultMessage: 'Do you want to book these items together?',
              description: 'Question asking if user wants to book bundled items together',
            })}
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'De9hF2O37C',
            defaultMessage: 'Yes, book tour and flights',
            description: 'Button to confirm booking tour and flights as a package',
          }),
          to: restoreCartUrl,
        }}
        secondaryActionProps={{
          children: unbundledText,
          to: unbundledURL,
        }}
      />
    </>
  )
}

export default BookTourFlight
